import React from "react";
import { useSelector } from "react-redux";

import { DailySchedule } from "custom-types/DispensarySchedules";
import { useEventTracker } from "hooks/useEventTracker";
import {
  getCurrentDispensary,
  getDispensarySchedules,
  getDispensaryUtm,
} from "redux/selectors/dispensary";
import { Action, Category } from "utils/trackEvent";

import ExpandableContainer from "components/botanic/ExpandableContainer";
import DispensaryFeatures from "components/Dispensary/AboutDispensary/DispensaryFeatures";
import PhotoOverview from "components/Dispensary/AboutDispensary/PhotoOverview";
import PrimaryAddress from "components/Dispensary/AboutDispensary/PrimaryAddress";
import PromotionOverview from "components/Dispensary/AboutDispensary/PromotionOverview";
import ReviewsOverview from "components/Dispensary/AboutDispensary/ReviewsOverview";
import SponsoredVideo from "components/Dispensary/AboutDispensary/SponsoredVideo";
import Updates from "components/Dispensary/AboutDispensary/Updates";
import RetailHours from "components/Dispensary/RetailHours";
import HeartOutlineIcon from "components/Icons/heart_outline.svg";
import OpenInNewIcon from "components/Icons/open_in_new.svg";
import PhoneIcon from "components/Icons/phone.svg";
import EmailIcon from "components/Icons/share/email.svg";

const AboutDispensary = () => {
  const { publishEvent } = useEventTracker();
  const dispensary = useSelector(getCurrentDispensary);
  const schedules = useSelector(getDispensarySchedules);
  const websiteWithUTM = useSelector(getDispensaryUtm);

  const {
    created,
    description,
    email,
    followerCount,
    licenseNumber,
    name,
    phone,
    retailType,
    sponsoredVideoId,
    country,
  } = dispensary;

  const joinedYear = new Date(created).getFullYear();

  const trackAboutEvent = (label: string) => {
    publishEvent({
      action: Action.click,
      category: Category.dispensaryFooter,
      dispensaryId: dispensary.id,
      label: label,
    });
  };

  return (
    <div className="bg-white" id="about-dispensary">
      <div className="container py-xxl">
        <div className="section__header">
          <h3 className="heading--m">
            About this {country === "CA" ? "store" : retailType}
          </h3>
        </div>
        <div className="mt-4 text-sm md:mt-0 row">
          <div className="md:col-4 mb-xl">
            <h4 className="text-xs uppercase mb-xs">{name}</h4>
            {description && (
              <ExpandableContainer
                initialHeight="216px"
                hide={description.length < 300}
              >
                <p className="text-sm break-words whitespace-pre-wrap">
                  {description}
                </p>
              </ExpandableContainer>
            )}
          </div>
          <div className="md:col-4 mb-xl">
            <h3 className="text-xs uppercase mb-sm">
              Leafly member since {joinedYear}
            </h3>
            <div className="mb-lg text-green">
              <div className="flex icon--centered mb-sm">
                <HeartOutlineIcon width="18" height="18" className="mr-sm" />
                Followers: {followerCount}
              </div>
              <PrimaryAddress />
              {email && (
                <div className="flex icon--centered mb-sm mt-sm">
                  <EmailIcon width="18" height="18" className="mr-sm" />
                  <a
                    data-testid={"about_dispensary__email"}
                    onClick={() => trackAboutEvent("email")}
                    href={`mailto:${email}`}
                  >
                    Send a message
                  </a>
                </div>
              )}
              {phone && (
                <div className="flex icon--centered mb-sm mt-sm">
                  <PhoneIcon width="18" height="18" className="mr-sm" />
                  <a
                    data-testid={"about_dispensary__call"}
                    onClick={() => trackAboutEvent("call")}
                    href={`tel:${phone}`}
                  >
                    Call {phone}
                  </a>
                </div>
              )}
              {websiteWithUTM && (
                <div className="flex icon--centered mt-sm mb-md">
                  <OpenInNewIcon width="18" height="18" className="mr-sm" />
                  <a
                    data-testid={"about_dispensary__visit_website"}
                    onClick={() => trackAboutEvent("visit website")}
                    href={websiteWithUTM}
                    rel="sponsored noreferrer"
                    target="_blank"
                  >
                    Visit website
                  </a>
                </div>
              )}
              {licenseNumber && (
                <div className="text-deep-green">
                  <strong>License</strong> {licenseNumber}
                </div>
              )}
            </div>
            <DispensaryFeatures dispensary={dispensary} />
          </div>
          <div className="md:col-4 mb-xl">
            <RetailHours
              timeZone={dispensary?.timeZone}
              schedule={schedules?.storeHours as DailySchedule[] | null}
            />
          </div>
        </div>
        <PhotoOverview className="mb-section" />
        <PromotionOverview className="mb-section" />
        <Updates className="mb-section" />
        <SponsoredVideo sponsoredVideoId={sponsoredVideoId} />
        <ReviewsOverview />
      </div>
    </div>
  );
};

export default AboutDispensary;
